import { Check, Languages, LogOut, Monitor, Moon, ShieldAlert, ShieldCheck, Sun } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { cn } from '@/lib/utils';
import { api } from '../../api';
import { clearSession, useSession } from '../../api/session';
import { USE_MOCKS } from '../../config/env';
import { LANGUAGES, setLanguage, useLanguage } from '../../i18n';
import { formatDateTime } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { useTheme, type ThemeChoice } from '../theme';
import { CommandMenu } from './CommandMenu';
import { DossierBadge } from './DossierBadge';
import { TestSystemBanner, useDeployment } from './TestSystemBanner';

const ENV_CLASS: Record<string, string> = {
  ote: 'border-warning/30 bg-warning-soft text-warning',
  production: 'border-critical/40 bg-critical-soft text-critical',
};

/** The server's own label (OT&E or production) once known; nothing before that, never a guess. */
function ServerBadge() {
  const { t } = useTranslation();
  const deployment = useDeployment();
  if (!deployment) return null;
  return (
    <Badge variant="outline" className={cn('hidden uppercase sm:inline-flex', ENV_CLASS[deployment.environment])} title={t('deployment.label', { env: t(`deployment.${deployment.environment}`) })}>
      {t(`deployment.${deployment.environment}`)}
    </Badge>
  );
}

function LanguageMenu() {
  const { t } = useTranslation();
  const lang = useLanguage();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-1.5 px-2" aria-label={t('lang.label')}>
          <Languages className="size-4" />
          <span className="text-xs font-semibold uppercase">{lang}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>{t('lang.label')}</DropdownMenuLabel>
        {LANGUAGES.map((l) => (
          <DropdownMenuItem key={l} onSelect={() => setLanguage(l)}>
            <Check className={cn('size-4', l === lang ? 'opacity-100' : 'opacity-0')} />
            {t(`lang.${l}`)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const THEME_ICONS: Record<ThemeChoice, typeof Sun> = { light: Sun, dark: Moon, system: Monitor };

function ThemeMenu() {
  const { t } = useTranslation();
  const { theme, setTheme } = useTheme();
  const Current = THEME_ICONS[theme];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="size-8" aria-label={t('theme.label')}>
          <Current className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>{t('theme.label')}</DropdownMenuLabel>
        {(['light', 'dark', 'system'] as const).map((choice) => {
          const Icon = THEME_ICONS[choice];
          return (
            <DropdownMenuItem key={choice} onSelect={() => setTheme(choice)}>
              <Icon className="size-4" />
              {t(`theme.${choice}`)}
              {theme === choice ? <Check className="ml-auto size-4" /> : null}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function UserMenu() {
  const { t } = useTranslation();
  const session = useSession();
  const navigate = useNavigate();
  const me = useApi((signal) => api.me({ signal }), [session?.token]);
  const [signingOut, setSigningOut] = useState(false);

  const signOut = async () => {
    setSigningOut(true);
    try {
      await api.logout();
    } catch {
      // The server session may already be gone; the local session is cleared either way.
    }
    clearSession('logout');
    navigate('/login', { replace: true });
  };

  const email = session?.email ?? '';
  const initials = (email.split('@')[0] ?? '').split(/[._-]/).map((p) => p.charAt(0)).join('').slice(0, 2).toUpperCase() || 'OP';
  const mfa = me.status === 'success' ? me.data.mfa_verified : (session?.mfa_verified ?? false);
  const roles = me.status === 'success' ? [...new Set(me.data.grants.map((g) => g.role))] : [];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="h-9 gap-2 px-1.5" aria-label={email || t('user.operator')}>
          <Avatar className="size-7">
            <AvatarFallback className="bg-primary/10 text-xs font-semibold text-primary">{initials}</AvatarFallback>
          </Avatar>
          <span className="hidden max-w-40 truncate text-sm xl:inline">{email || t('user.operator')}</span>
          {!mfa ? <ShieldAlert className="size-4 text-warning" aria-label={t('user.noMfa')} /> : null}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuLabel className="space-y-1.5 font-normal">
          <div className="truncate text-sm font-semibold">{email || t('user.signedIn')}</div>
          {me.status === 'success' ? (
            <div className="text-xs text-muted-foreground">
              {t('user.user')} <code title={me.data.user_id}>{me.data.user_id.slice(0, 8)}…</code>
            </div>
          ) : me.status === 'error' ? (
            <div className="text-xs text-destructive">{t('user.loadFailed')}</div>
          ) : null}
          <div>
            {mfa ? (
              <Badge variant="outline" className="gap-1 border-success/30 bg-success-soft text-success">
                <ShieldCheck className="size-3" />
                {t('user.mfaVerified')}
              </Badge>
            ) : (
              <Badge variant="outline" className="gap-1 border-warning/30 bg-warning-soft text-warning" title={t('user.noMfaHint')}>
                <ShieldAlert className="size-3" />
                {t('user.noMfa')}
              </Badge>
            )}
          </div>
          {roles.length ? (
            <div className="flex flex-wrap gap-1">
              {roles.map((r) => (
                <Badge key={r} variant="secondary" className="font-mono text-[10px]">
                  {r}
                </Badge>
              ))}
            </div>
          ) : null}
          {session ? <div className="text-xs text-muted-foreground">{t('user.expires', { at: formatDateTime(session.expires_at) })}</div> : null}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {!mfa ? (
          <DropdownMenuItem asChild>
            <Link to="/login?mfa=1">
              <ShieldCheck className="size-4" />
              {t('user.reauth')}
            </Link>
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuItem onSelect={() => void signOut()} disabled={signingOut} variant="destructive">
          <LogOut className="size-4" />
          {signingOut ? t('user.signingOut') : t('user.signOut')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function TopBar() {
  const { t } = useTranslation();
  return (
    // The OT&E strip sits in the sticky header so it stays visible on every page and scroll position.
    <div className="sticky top-0 z-30">
      <TestSystemBanner />
      <header className="flex h-16 shrink-0 items-center gap-2 border-b bg-card/95 px-3 backdrop-blur supports-[backdrop-filter]:bg-card/80 md:px-6">
        <SidebarTrigger className="-ml-1" />
        <Separator orientation="vertical" className="mr-1 data-[orientation=vertical]:h-5" />
        <div className="flex min-w-0 flex-1 basis-0 justify-center px-1 sm:min-w-40 sm:px-2">
          <CommandMenu />
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <DossierBadge />
          <ServerBadge />
          {USE_MOCKS ? (
            <Badge variant="outline" className="hidden border-serious/30 bg-serious-soft text-serious xl:inline-flex" title={t('env.mockHint')}>
              {t('env.mock')}
            </Badge>
          ) : null}
          <LanguageMenu />
          <ThemeMenu />
          <UserMenu />
        </div>
      </header>
    </div>
  );
}
